/**
 * AERO - Store policy pages (snippets/policy-page.liquid)
 *
 * Builds the on-page index for /policies/*.
 *
 * The legal text lives in the Shopify admin (Settings > Policies) and reaches
 * the page as {{ policy.body }}, so nothing here may assume a fixed
 * structure. As written today: the privacy, refund and shipping policies are
 * sectioned with <h2>, the terms of service with numbered <h3>, and the
 * contact information policy has no headings at all. The index is derived from
 * whatever the admin actually rendered, so editing a policy never means
 * editing the theme.
 *
 * Pure logic is exported for Node, mirroring assets/help.js and
 * assets/cart-utils.js. Everything below the divider needs a browser.
 */
(function () {
  'use strict';

  /* ===== Pure logic (no DOM access below this line) ===== */

  var ID_PREFIX = 'sec-';

  /* An index entry needs company: a one-item list is furniture, not an index. */
  var MIN_ENTRIES = 2;

  function collapse(text) {
    if (text === null || text === undefined) return '';
    return String(text).replace(/\s+/g, ' ').trim();
  }

  /*
   * Slug for one heading. Diacritics are folded rather than dropped, so
   * "Quién trata tus datos" becomes "quien-trata-tus-datos" and not
   * "qui-n-trata-tus-datos".
   */
  function slugify(text) {
    return collapse(text)
      .toLowerCase()
      .normalize('NFD')
      // U+0300-U+036F is the combining-diacritics block. Written escaped on
      // purpose: the literal characters are invisible and corrupt on copy.
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /*
   * Element id for a heading.
   *
   * Always prefixed: the terms of service numbers its sections, so
   * "1. Quiénes somos" slugifies to "1-quienes-somos", and an id starting with
   * a digit is legal HTML but an invalid CSS selector — querySelector('#1-...')
   * throws. The prefix keeps every id usable from both getElementById and
   * querySelector, and keeps these ids clear of the ones the layout already
   * owns.
   *
   * `used` is read and appended to: it records the ids handed out so far, so
   * two sections worded the same do not end up sharing an anchor.
   */
  function toId(text, used) {
    var base = slugify(text) || 'seccion';
    var id = ID_PREFIX + base;
    var suffix = 2;
    while (used.indexOf(id) !== -1) {
      id = ID_PREFIX + base + '-' + suffix;
      suffix += 1;
    }
    used.push(id);
    return id;
  }

  /* The shallowest heading level present, or 0 when there are none. */
  function topLevel(headings) {
    if (!headings || headings.length === 0) return 0;
    return headings.reduce(function (top, heading) {
      var level = Number(heading.level);
      if (!level) return top;
      return top === 0 || level < top ? level : top;
    }, 0);
  }

  /*
   * [{ level, text }] in document order -> [{ index, text, id }].
   *
   * Only the shallowest level present becomes an entry, so the numbered <h3>s
   * of the terms of service and the <h2>s of the privacy policy each produce a
   * flat list without either page having to declare which one it is. `index`
   * is the position in the input array, so the caller can put the id back on
   * the right element.
   *
   * Returns [] rather than a stub when there is nothing worth indexing — the
   * contact information policy has no headings, and the caller lays the page
   * out as a single column in that case.
   */
  function buildToc(headings) {
    var top = topLevel(headings);
    if (top === 0) return [];

    var used = [];
    var entries = [];

    headings.forEach(function (heading, index) {
      if (Number(heading.level) !== top) return;
      var text = collapse(heading.text);
      if (text === '') return;
      entries.push({ index: index, text: text, id: toId(text, used) });
    });

    return entries.length < MIN_ENTRIES ? [] : entries;
  }

  /*
   * Which section the reader is currently in, given every heading's distance
   * from the top of the viewport and the line it has to cross to count.
   *
   * The active section is the last one whose heading has already passed the
   * line — which is what "the section I am reading" means, and is not the same
   * question as "which heading is visible": a section long enough to fill the
   * screen has no heading on it at all. Returns -1 above the first heading.
   */
  function activeIndex(offsets, line) {
    var active = -1;
    for (var i = 0; i < offsets.length; i++) {
      if (offsets[i] - line <= 0) active = i;
    }
    return active;
  }

  /*
   * Slack added to the line that decides which section is being read.
   *
   * Following an index entry scrolls its heading to exactly scroll-margin-top,
   * which is the very same offset the line is set to — and
   * getBoundingClientRect() then reports something like 91.34 rather than 91,
   * so a strict comparison leaves the section the reader just asked for
   * unlit and highlights the one above it. Measured in Chrome at 390px and
   * 1440px: without this, clicking an entry lights the previous entry. A few
   * pixels of slack removes the boundary and costs nothing.
   */
  var ACTIVE_SLACK = 4;

  function activeLine(chromeTop) {
    return (Number(chromeTop) || 0) + ACTIVE_SLACK;
  }

  var api = {
    collapse: collapse,
    slugify: slugify,
    toId: toId,
    topLevel: topLevel,
    buildToc: buildToc,
    activeIndex: activeIndex,
    activeLine: activeLine,
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.AeroPolicyToc = api;

  /* ===== DOM layer — everything below needs a browser ===== */

  if (typeof document === 'undefined') return;

  /* Kept in step with the single-column breakpoint in assets/policy.css. */
  var MOBILE_QUERY = '(max-width: 900px)';

  /* Run `paint` at most once per frame, on a passive listener. */
  function onFrame(paint) {
    var ticking = false;
    return function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        paint();
      });
    };
  }

  /*
   * Back to top. Only mobile shows it (policy.css hides it on wide screens,
   * where the index is a fixed column and already does this job), and only
   * once there is a screenful to come back from.
   */
  function initBackToTop(link) {
    var shown = null;

    var paint = function () {
      var should = window.pageYOffset > window.innerHeight;
      if (should === shown) return;
      shown = should;
      link.hidden = !should;
    };

    window.addEventListener('scroll', onFrame(paint), { passive: true });
    paint();
  }

  /* Highlight the entry for the section being read. */
  function initScrollSpy(root, sections, links) {
    var active = null;

    var paint = function () {
      /* The line a heading crosses to count as "here": under the sticky
         navbar, the same offset the headings use as scroll-margin-top, plus
         the slack that keeps the boundary off a sub-pixel coin flip. */
      var line =
        parseFloat(
          window.getComputedStyle(root).getPropertyValue('--policy-chrome-top')
        ) || 0;

      var offsets = sections.map(function (section) {
        return section.getBoundingClientRect().top;
      });

      var index = api.activeIndex(offsets, api.activeLine(line));
      var next = index === -1 ? null : links[index];
      if (next === active) return;

      if (active) {
        active.classList.remove('is-active');
        active.removeAttribute('aria-current');
      }
      if (next) {
        next.classList.add('is-active');
        next.setAttribute('aria-current', 'true');
      }
      active = next;
    };

    var scheduled = onFrame(paint);
    window.addEventListener('scroll', scheduled, { passive: true });
    window.addEventListener('resize', scheduled, { passive: true });
    paint();
  }

  /*
   * The index is a disclosure on a narrow screen and a fixed column on a wide
   * one, from the same markup. It is rendered open so that it is complete and
   * usable with no JavaScript at all; this closes it only where it would
   * otherwise push the policy itself off the first screen.
   */
  function initDisclosure(details) {
    if (!details || !window.matchMedia) return;

    var mobile = window.matchMedia(MOBILE_QUERY);

    var sync = function (isMobile) {
      details.open = !isMobile;
    };

    sync(mobile.matches);

    /* On a wide screen the summary is a label, not a control: policy.css
       makes it ignore the mouse, but it is still a <summary> and still
       answers Enter. Closing it there would empty the column. */
    details.addEventListener('toggle', function () {
      if (!details.open && !mobile.matches) details.open = true;
    });

    if (mobile.addEventListener) {
      mobile.addEventListener('change', function (event) {
        sync(event.matches);
      });
    } else if (mobile.addListener) {
      /* Safari < 14 never got the EventTarget interface on MediaQueryList. */
      mobile.addListener(function (event) {
        sync(event.matches);
      });
    }

    /*
     * Deliberately no "collapse after picking an entry". On a narrow screen
     * the index is a static block at the top of the document, so following an
     * entry scrolls past it anyway — and collapsing it would shorten the page
     * above the target while the smooth scroll is still in flight, landing the
     * reader short of the heading they asked for.
     */
  }

  function init() {
    var root = document.querySelector('.aero-policy');
    if (!root) return;

    var topLink = root.querySelector('[data-policy-top]');
    if (topLink) initBackToTop(topLink);

    var content = root.querySelector('[data-policy-content]');
    var nav = root.querySelector('[data-policy-toc]');
    var list = root.querySelector('[data-policy-toc-list]');
    if (!content || !nav || !list) return;

    var headings = Array.prototype.slice.call(content.querySelectorAll('h2, h3, h4'));
    var entries = api.buildToc(
      headings.map(function (heading) {
        return { level: Number(heading.tagName.slice(1)), text: heading.textContent };
      })
    );

    if (entries.length === 0) {
      /* Nothing to index — the policy gets the full width to itself. */
      root.classList.add('aero-policy--no-toc');
      return;
    }

    var sections = entries.map(function (entry) {
      var heading = headings[entry.index];
      heading.id = entry.id;
      /* Anchor navigation moves focus only to a focusable target. Without
         this the keyboard and the screen-reader cursor stay on the link that
         was just followed, and the next Tab continues through the index. */
      heading.setAttribute('tabindex', '-1');
      return heading;
    });

    var links = entries.map(function (entry) {
      var item = document.createElement('li');
      item.className = 'aero-policy__toc-item';

      var link = document.createElement('a');
      link.className = 'aero-policy__toc-link';
      link.href = '#' + entry.id;
      link.textContent = entry.text;

      item.appendChild(link);
      list.appendChild(item);
      return link;
    });

    root.classList.add('aero-policy--has-toc');
    nav.hidden = false;

    initDisclosure(root.querySelector('[data-policy-toc-details]'));
    initScrollSpy(root, sections, links);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
